import { getUsersMe, postAuthGetSession } from '@repo/api-portal-sdk/gen'
import type { RouteSdkOptions } from '@repo/ssr'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import { normalizePortalSession } from './portal-session-state'
import { makeQueryKeys } from './query-keys'

export function fetchFreshPortalSession(queryClient: QueryClient) {
	return queryClient.fetchQuery({
		...portalSessionQueryOptions(),
		staleTime: 0
	})
}

export function portalSessionQueryOptions(sdkOptions?: RouteSdkOptions) {
	return queryOptions({
		gcTime: Number.POSITIVE_INFINITY,
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: makeQueryKeys.auth.session(),
		queryFn: async ({ signal }) => {
			const response = await postAuthGetSession(
				withQuerySignal(sdkOptions, signal)
			)
			return normalizePortalSession(response.data)
		}
	})
}

export function portalMeQueryOptions(sdkOptions?: RouteSdkOptions) {
	return queryOptions({
		queryKey: makeQueryKeys.users.me(),
		queryFn: async ({ signal }) => {
			const response = await getUsersMe(
				withQuerySignal(sdkOptions, signal)
			)
			return response.data.data
		}
	})
}

export async function fetchPortalLandingSessionState(
	sdkOptions?: RouteSdkOptions
) {
	try {
		const sessionResponse = await postAuthGetSession(sdkOptions)
		const session = normalizePortalSession(sessionResponse.data)

		if (
			session.sessionType !== 'authenticated' ||
			session.user.isAnonymous
		) {
			return { status: 'guest' as const }
		}

		const meResponse = await getUsersMe(
			withCsrfToken(sdkOptions, session.csrfToken)
		)
		return {
			status: 'authenticated' as const,
			csrfToken: session.csrfToken,
			user: meResponse.data.data
		}
	} catch (error) {
		if (sdkOptions?.signal?.aborted) {
			throw error
		}

		if (isUnauthenticatedError(error)) {
			return { status: 'guest' as const }
		}

		return { status: 'unknown' as const }
	}
}

function withQuerySignal(
	sdkOptions: RouteSdkOptions | undefined,
	signal: AbortSignal | undefined
) {
	if (!signal) {
		return sdkOptions
	}

	return { ...sdkOptions, signal }
}

function withCsrfToken(
	sdkOptions: RouteSdkOptions | undefined,
	csrfToken: string
) {
	const headers = new Headers(sdkOptions?.headers)
	headers.set('x-csrf-token', csrfToken)

	return { ...sdkOptions, headers }
}

function isUnauthenticatedError(error: unknown) {
	if (
		!error ||
		typeof error !== 'object' ||
		!('code' in error) ||
		typeof error.code !== 'string'
	) {
		return false
	}

	return [
		'auth_credentials_missing',
		'auth_jwt_invalid',
		'session_invalid',
		'session_expired',
		'user_deleted'
	].includes(error.code)
}

export type PortalLandingSessionState = Awaited<
	ReturnType<typeof fetchPortalLandingSessionState>
>

export function portalLandingSessionQueryOptions(
	initialState?: PortalLandingSessionState
) {
	return queryOptions({
		queryKey: makeQueryKeys.auth.landingSession(),
		queryFn: ({ signal }) => fetchPortalLandingSessionState({ signal }),
		initialData:
			initialState?.status === 'unknown' ? undefined : initialState,
		staleTime:
			initialState?.status === 'unknown' ? 0 : Number.POSITIVE_INFINITY,
		retry: false
	})
}
